<template>
  <b-container class="details mb-2">
    <template v-if="!isPureMop">
      <b-row class="mb-2 tabHeader">
        <b-col cols="12" class="font-weight-bold" v-t="'productDetails.tabHeader.costsProfile'"></b-col>
      </b-row>
      <template v-if="isMop">
        <b-row>
          <b-col cols="12">
            <b-table
              striped
              hover
              :items="riyResults"
              :fields="riyShownFields"
              class="results w-100"
              primary-key="id"
              data-test="riyTable"
            >
              <template v-slot:cell(period1)="row">
                {{ formatPercentage(kpi.period1['costs' + row.item.field].riy) }}
              </template>
              <template v-slot:cell(period2)="row">
                {{ formatPercentage(kpi.period2['costs' + row.item.field].riy) }}
              </template>
              <template v-slot:cell(period3)="row">
                {{ formatPercentage(kpi.period3['costs' + row.item.field].riy) }}
              </template>
            </b-table>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <b-table
              striped
              hover
              :items="totalCostsResults"
              :fields="totalCostsShownFields"
              class="results w-100"
              primary-key="id"
              data-test="totalCostsTable"
            >
              <template v-slot:cell(period1)="row">
                {{ formatCurrency(kpi.period1['costs' + row.item.field].cost) }}
              </template>
              <template v-slot:cell(period2)="row">
                {{ formatCurrency(kpi.period2['costs' + row.item.field].cost) }}
              </template>
              <template v-slot:cell(period3)="row">
                {{ formatCurrency(kpi.period3['costs' + row.item.field].cost) }}
              </template>
            </b-table>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-row>
          <b-col cols="12">
            <b-table
              striped
              hover
              :items="mixedResults"
              :fields="mixedShownFields"
              class="results w-100"
              primary-key="id"
              data-test="voiceCostTable"
            >
              <template v-slot:cell(period1)="row">
                {{
                  row.item.field === 'riy'
                    ? formatPercentage(kpi.period1['costsMin'][row.item.field])
                    : formatCurrency(kpi.period1['costsMin'][row.item.field])
                }}
              </template>
              <template v-slot:cell(period2)="row">
                {{
                  row.item.field === 'riy'
                    ? formatPercentage(kpi.period2['costsMin'][row.item.field])
                    : formatCurrency(kpi.period2['costsMin'][row.item.field])
                }}
              </template>
              <template v-slot:cell(period3)="row">
                {{
                  row.item.field === 'riy'
                    ? formatPercentage(kpi.period3['costsMin'][row.item.field])
                    : formatCurrency(kpi.period3['costsMin'][row.item.field])
                }}
              </template>
            </b-table>
          </b-col>
        </b-row>
      </template>
    </template>
    <template v-if="!hideMaturityCosts">
      <b-row class="mb-2 tabHeader">
        <b-col cols="12" class="font-weight-bold" v-t="'productDetails.tabHeader.maturityCostsProfile'"></b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-table
            striped
            hover
            :items="maturityCostsResults"
            :fields="maturityCostsShownFields"
            class="results w-100"
            primary-key="id"
            data-test="maturityCostsResults"
          >
            <template v-slot:cell(min)="row">
              {{ formatPercentage(expCosts[row.item.field + 'Min']) }}
            </template>
            <template v-slot:cell(max)="row">
              {{ formatPercentage(expCosts[row.item.field + 'Max']) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>
<script>
export default {
  name: 'ProductCosts',
  props: {
    value: {},
    currency: {},
    kidType: {},
    isMop: { type: Boolean, default: false },
    // Lets a caller show only the "Profilo dei costi" table, skipping "Costi a scadenza" —
    // the config-driven detail page renders that as its own separate section
    // (CostsAtExpiration, a different catalog SectionKey) instead of bundling it into this
    // component's own second table. Default false.
    hideMaturityCosts: { type: Boolean, default: false },
    // See periodsHeaders() below.
    firstPeriodLabel: { type: String, default: null },
  },
  computed: {
    kpi: function () {
      return this.value
    },
    expCosts: function () {
      return this.value.expirationCosts
    },
    riyShownFields: function () {
      if (!this.isMop) return []

      return [
        {
          key: 'label',
          label: this.$t('search.resultTable.riy'),
          class: 'dataname text-left',
        },
        ...this.periodsHeaders(),
      ]
    },
    totalCostsShownFields: function () {
      if (!this.isMop) return []

      return [
        {
          key: 'label',
          label: this.$t('search.label.totalCosts'),
        },
        ...this.periodsHeaders(),
      ]
    },
    mixedShownFields: function () {
      if (this.isMop) return []

      return [
        {
          key: 'label',
          label: this.$t('common.costsOverTime'),
          class: 'dataname text-left',
        },
        ...this.periodsHeaders(),
      ]
    },
    maturityCostsShownFields: function () {
      let temp = [
        {
          key: 'label',
          label: this.$t('productDetails.voiceCost'),
          class: 'dataname text-left',
        },
        {
          key: 'empty',
          label: '',
        },
      ]

      if (this.isMop) {
        temp.push(
          {
            key: 'min',
            label: 'Min',
            class: 'text-right',
          },
          {
            key: 'max',
            label: 'Max',
            class: 'text-right',
          }
        )
      } else {
        temp.push(
          {
            key: 'empty2',
            label: '',
          },
          {
            key: 'min',
            label: '',
            class: 'text-right',
          }
        )
      }
      return temp
    },
    riyResults: function () {
      return [
        {
          field: 'Min',
          label: this.$t('search.resultTable.riyMin'),
          period1: this.kpi.period1.costsMin ? this.kpi.period1.costsMin.riy : null,
          period2: this.kpi.period2.costsMin ? this.kpi.period2.costsMin.riy : null,
          period3: this.kpi.period3.costsMin ? this.kpi.period3.costsMin.riy : null,
        },
        {
          field: 'Max',
          label: this.$t('search.resultTable.riyMax'),
          period1: this.kpi.period1.costsMax ? this.kpi.period1.costsMax.riy : null,
          period2: this.kpi.period2.costsMax ? this.kpi.period2.costsMax.riy : null,
          period3: this.kpi.period3.costsMax ? this.kpi.period3.costsMax.riy : null,
        },
      ]
    },
    totalCostsResults: function () {
      return [
        {
          field: 'Min',
          label: this.$t('search.resultTable.costsMin'),
          period1: this.kpi.period1.costsMin ? this.kpi.period1.costsMin.cost : null,
          period2: this.kpi.period2.costsMin ? this.kpi.period2.costsMin.cost : null,
          period3: this.kpi.period3.costsMin ? this.kpi.period3.costsMin.cost : null,
        },
        {
          field: 'Max',
          label: this.$t('search.resultTable.costsMax'),
          period1: this.kpi.period1.costsMax ? this.kpi.period1.costsMax.cost : null,
          period2: this.kpi.period2.costsMax ? this.kpi.period2.costsMax.cost : null,
          period3: this.kpi.period3.costsMax ? this.kpi.period3.costsMax.cost : null,
        },
      ]
    },
    mixedResults: function () {
      return [
        {
          field: 'riy',
          formatter: (val) => this.formatPercentage(val),
          label: this.$t('search.resultTable.riy'),
          period1:
            this.kpi.period1.costsMin !== null
              ? this.kpi.period1.costsMin !== -1
                ? this.$n(this.kpi.period1.costsMin, 'percent')
                : '-'
              : null,
          period2:
            this.kpi.period2.costsMin !== null
              ? this.kpi.period2.costsMin !== -1
                ? this.$n(this.kpi.period2.costsMin, 'percent')
                : '-'
              : null,
          period3:
            this.kpi.period3.costsMin !== null
              ? this.kpi.period3.costsMin !== -1
                ? this.$n(this.kpi.period3.costsMin, 'percent')
                : '-'
              : null,
        },
        {
          field: 'cost',
          formatter: (val) => this.formatCurrency(val),
          label: this.$t('search.resultTable.costs'),
          period1: this.kpi.period1.costsMin
            ? this.$n(this.kpi.period1.costMin, { key: 'currency', currency: this.currency })
            : null,
          period2: this.kpi.period2.costsMin
            ? this.$n(this.kpi.period2.costMin, { key: 'currency', currency: this.currency })
            : null,
          period3: this.kpi.period3.costsMin
            ? this.$n(this.kpi.period3.costMin, { key: 'currency', currency: this.currency })
            : null,
        },
      ]
    },
    maturityCostsResults: function () {
      return Object.keys(this.expCosts)
        .filter((el) => el.includes('Min'))
        .map((field) => {
          field = field.replace(/Min$/, '')

          if (this.isMop) {
            return {
              field: field,
              label: this.$t('productDetails.costLabels.' + field),
              min: this.expCosts[field + 'Min'],
              max: this.expCosts[field + 'Max'],
            }
          } else {
            return {
              field: field,
              label: this.$t('productDetails.costLabels.' + field),
              min: this.expCosts[field + 'Min'],
            }
          }
        })
    },
    isPureMop() {
      return this.kidType === 1
    },
  },
  methods: {
    periodsHeaders() {
      const period1 = {
        key: this.kpi.period1.costsYear != null ? 'period1' : ' ',
        // firstPeriodLabel overrides the year-based label with e.g. "Prima data di richiamo
        // anticipato" for an autocallable/callable IC product. Unset (null) everywhere else, falling
        // through to the year-string label.
        label: this.firstPeriodLabel || (this.kpi.period1.costsYear ? this.kpi.period1.costsYear.toString() : ''),
        class: 'text-right',
      }
      const period2 = {
        key: this.kpi.period2.costsYear != null ? 'period2' : ' ',
        label:
          this.kpi.period2.costsYear != null
            ? this.kpi.period2.costsYear !== 0
              ? this.kpi.period2.costsYear.toString()
              : 'RHP/2'
            : '',
        class: 'text-right',
      }
      const period3 = {
        key: this.kpi.period3.costsYear != null ? 'period3' : ' ',
        label:
          this.kpi.period3.costsYear != null
            ? this.kpi.period3.costsYear !== 0
              ? this.kpi.period3.costsYear.toString()
              : 'RHP'
            : '',
        class: 'text-right',
      }
      const emptyPeriod = {
        label: '',
      }

      const ret = [period1]
      if (this.kpi.period2.costsYear !== null) {
        ret.push(period2)
      } else {
        ret.unshift(emptyPeriod)
      }
      if (this.kpi.period3.costsYear !== null) {
        ret.push(period3)
      } else {
        ret.unshift(emptyPeriod)
      }
      return ret
    },
    formatPercentage(val) {
      return val != undefined && val != null && !isNaN(val) ? this.$n(val, 'percent') : '-'
    },
    formatCurrency(val) {
      return val != undefined && val != null && !isNaN(val)
        ? this.$n(val, { key: 'currency', currency: this.currency })
        : '-'
    },
  },
}
</script>
<style lang="scss" scoped></style>
